import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { typedAs } from 'shared/src/utils/typedAs'
import { isMobile } from 'react-device-detect'

import logo from '../../assets/share-media-logo.svg'
import lasso from '../../assets/share-media-lasso.svg'
import { Modal } from 'components/Modal'
import { BODY_MEDIUM_STYLE, BUTTONS_STYLE } from 'components/styles'
import Icon from 'components/Icon'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { TextFieldNew } from 'components/TextField'

const MAX_TEXT_LENGTH = 50
const MAX_HEIGHT = 1920
const MAX_WIDTH = 1080

const resizeText = (text: string, ctx: CanvasRenderingContext2D) => {
  const maxHeight = MAX_HEIGHT
  let fontSize = 380
  ctx.font = `660 ${fontSize}px Playfair Display, serif`
  while (ctx.measureText(text).width > maxHeight && fontSize > 2) {
    ctx.font = `${(fontSize -= 2)}px Playfair Display, serif`
  }
}

const createCanvas = async (
  imageUrl: string,
  displayText: string
): Promise<HTMLCanvasElement | undefined> => {
  let canvas = document.createElement('canvas')

  if (!canvas) {
    return undefined
  }

  canvas.width = MAX_WIDTH
  canvas.height = MAX_HEIGHT

  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return undefined
  }

  ctx.fillStyle = '#282727'
  ctx.fillRect(0, 0, MAX_WIDTH, MAX_HEIGHT)

  const drawImage = async (): Promise<boolean> =>
    new Promise((resolve) => {
      if (imageUrl) {
        const image = new Image()
        image.src = imageUrl
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          const imageDisplayHeight = (910 / image.width) * image.height
          ctx.drawImage(
            image,
            0,
            (MAX_HEIGHT - imageDisplayHeight) / 2,
            910,
            imageDisplayHeight
          )
          resolve(true)
        }
      }
    })

  const drawLogo = async (): Promise<boolean> =>
    new Promise((resolve) => {
      const logoImg = new Image()
      logoImg.src = logo
      logoImg.onload = () => {
        ctx.drawImage(logoImg, 0, 0)
        resolve(true)
      }
    })

  const drawLasso = async (): Promise<boolean> =>
    new Promise((resolve) => {
      const lassoImage = new Image()
      lassoImage.src = lasso
      lassoImage.onload = () => {
        ctx.drawImage(lassoImage, 0, 408)
        resolve(true)
      }
    })

  try {
    await drawImage()
    await drawLasso()
    await drawLogo()

    ctx.fillStyle = '#FFFFFF'
    resizeText(displayText, ctx)
    ctx.textAlign = 'right'
    ctx.textBaseline = 'alphabetic'
    ctx.save()
    ctx.rotate(-Math.PI / 2)
    ctx.fillText(displayText, 0, canvas.width)
    ctx.restore()

    return canvas
  } catch (error) {
    return undefined
  }
}

const useStyles = createUseStyles({
  modalContainer: {
    height: '100dvh',
    background: '#F9F6F4',
    paddingLeft: 24,
    paddingRight: 24,
  },
  closeIcon: {
    position: 'absolute',
    right: 40,
    top: 16,
    color: '#90734F',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 36,
  },
  inputContainer: {
    width: '100%',
    display: 'flex',
    marginTop: 30,
  },
  inputText: {
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.96)',
    background: 'rgba(54, 52, 50, 0.02)',
    borderRadius: 2,
    height: 48,
    width: '100%',
    paddingRight: 12,
  },
  previewButton: {
    width: 56,
    height: 64,
    background: 'rgba(203, 166, 122, 0.24)',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewImage: {
    width: 238.5,
    height: 424,
    boxShadow: '0px 22.141px 17.7128px -13.2846px rgba(0, 0, 0, 0.24)',
    borderRadius: 8.8564,
    background: 'rgba(54, 52, 50, .02)',
  },
  shareButton: {
    width: '100%',
    height: 64,
    marginTop: 24,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(54, 52, 50, 0.96)',
    ...BUTTONS_STYLE,
    color: 'white',
    gap: 12,
  },
})

interface Props {
  displayText: string
  imageUrl: string
  onClose: () => void
}

export const ShareMediaModal: FC<Props> = flowMax(
  addDisplayName('ShareMediaModal'),
  addStateHandlers(
    ({ displayText }) => ({
      inputText: displayText,
    }),
    {
      onChangeText: () => (displayText: string) => ({
        inputText: displayText.substring(0, MAX_TEXT_LENGTH),
      }),
    }
  ),
  addState('canvas', 'setCanvas', typedAs<HTMLCanvasElement | null>(null)),
  addTranslationHelpers,
  addHandlers({
    onShare:
      ({ canvas, t }) =>
      () => {
        if (!canvas) {
          console.log('Canvas is not defined')
          return
        }

        if (isMobile && navigator.share) {
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const file = new File(
                  [blob],
                  t('admin.myRodeo.shareMediaFileName'),
                  {
                    type: 'jpeg',
                  }
                )

                navigator
                  .share({
                    files: [file],
                  })
                  .catch(() => console.log('Share canceled or failed'))
              }
            },
            'image/jpeg',
            1.0
          )
        } else {
          const dataUrl = canvas.toDataURL('image/jpeg', 1.0)
          const link = document.createElement('a')
          link.download = t('admin.myRodeo.shareMediaFileName')
          link.href = dataUrl

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      },
  }),
  addState('imageSrc', 'setImageSrc', typedAs<string | null>(null)),
  addHandlers({
    updateCanvas:
      ({ setImageSrc, imageUrl, inputText, setCanvas }) =>
      () => {
        createCanvas(imageUrl, inputText).then((canvas) => {
          if (!canvas) {
            return
          }

          setCanvas(canvas)

          setImageSrc(canvas.toDataURL('image/png', 1.0))
        })
      },
  }),
  addEffectOnMount(({ updateCanvas }) => () => {
    updateCanvas()
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    inputText,
    imageSrc,
    onChangeText,
    updateCanvas,
    onShare,
    onClose,
    classes,
    t,
  }) => (
    <Modal containerClassName={classes.modalContainer}>
      <Icon name="close" onClick={onClose} className={classes.closeIcon} />
      <div className={classes.contentContainer}>
        {imageSrc ? (
          <img
            src={imageSrc ?? ''}
            className={classes.previewImage}
            alt={t('admin.myRodeo.shareMediaPreviewAlt')}
          />
        ) : (
          <div className={classes.previewImage} />
        )}
        <div className={classes.inputContainer}>
          <TextFieldNew
            type="text"
            value={inputText}
            label={t('admin.myRodeo.shareMediaTextLabel')}
            onChange={onChangeText}
            className={classes.inputText}
          />
          <button onClick={updateCanvas} className={classes.previewButton}>
            <Icon name="clockwise-arrows" />
          </button>
        </div>
        <button onClick={onShare} className={classes.shareButton}>
          <Icon name="share" />
          <span>{t('admin.myRodeo.share')}</span>
        </button>
      </div>
    </Modal>
  )
)
